import { Avatar, Box, Button, Group, SimpleGrid, Text } from "@mantine/core"
import { ArrowLeftIcon, BuildingIcon, CalendarBlankIcon, IdentificationCardIcon, MapPinIcon, UserIcon } from "@phosphor-icons/react"
import { ReactNode, useEffect } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { HelpIcon, StatusBadgeHelp, useHelpContext } from "@dehub/theme"
import { IAuth } from "../../store/Auth"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import { PageLayout } from "../layout"
import { DotTag } from "../purchase-sales/status-tag"
import { CustomerOrdersSection } from "../supplier-customer-orders"
import classes from "./detail-form.module.css"
import { NetworkConfigOverridePanel } from "./config-override-panel"

type Props = {
  action: any[]
}

function Cell({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <Box className={classes.cell}>
      <Box className={classes.cellLabel}>{icon}<Text span inherit>{label}</Text></Box>
      <Box className={classes.cellValue}>{children}</Box>
    </Box>
  )
}

function Person({ staff }: { staff?: { avatar?: string | null } | null }) {
  const name = renderName(staff as never)
  if (!name) return <>-</>
  return (
    <Group gap="sm" wrap="nowrap">
      <Avatar src={staff?.avatar || null} size={28} radius="xl"><UserIcon size={14} /></Avatar>
      <Text span inherit>{name}</Text>
    </Group>
  )
}

export function SupplierCustomerDetailForm({ action }: Props) {
  const { t } = useTranslation(["supplierSales", "common"])
  const navigate = useNavigate()
  const myBusinessId = useSelector((s: { auth: IAuth }) => s.auth.user?.currentBusinessId)

  const record = action[1]
  // GET /ord/network/:id spreads the counterparty business, so `id` is that business and `networkId` is the
  // de_network row the config-override endpoints are keyed on (O74).
  const customerBusinessId: string | undefined = record?.coBusinessId ?? record?.id
  const isActive = (record?.networkStatus ?? "").toLowerCase() === "active" || record?.networkStatus === "Идэвхтэй"
  const c = (key: string) => t(`supplierSales:customer.${key}`)
  // @dehub/help: the link status drives the drawer's status tab (order.network-status).
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    setHelpContext({ status: isActive ? "ACTIVE" : "INACTIVE" })
  }, [isActive, setHelpContext])

  return (
    <PageLayout
      title={c("detailTitle")}
      subTitle={c("detailSubtitle")}
      breadcrumb={[
        { to: "/", label: t("common:breadcrumb.home") },
        { to: "/supplier-customer", label: c("title") },
        { label: c("detailTitle") },
      ]}
      extra={[
        <Button key="back" variant="default" leftSection={<ArrowLeftIcon size={16} />} onClick={() => navigate("/supplier-customer")}>
          {c("backButton")}
        </Button>,
      ]}>
      <Box className={classes.card}>
        <Group className={classes.cardHead} gap="md" wrap="wrap">
          <StatusBadgeHelp helpNs="order.network-status" code={isActive ? "ACTIVE" : "INACTIVE"} name={isActive ? c("active") : c("inactive")}>
            <DotTag color={isActive ? "green" : "gray"} label={isActive ? c("active") : c("inactive")} />
          </StatusBadgeHelp>
          <Text className={classes.cellCode}>{record?.refCode ?? "-"}</Text>
          <Text className={classes.profileName}>{renderBusinessName(record, "") || renderBusinessName(record?.partner, "-")}</Text>
          <HelpIcon helpKey="order.supplier-customer-detail.section.info" variant="section" />
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={0}>
          <Cell icon={<BuildingIcon size={16} />} label={c("partnerInfo")}>
            <Text className={classes.cellCode}>{record?.partner?.refCode || "-"}</Text>
            {renderBusinessName(record?.partner, "-")}
          </Cell>
          <Cell icon={<BuildingIcon size={16} />} label={c("customerInfo")}>
            <Text className={classes.cellCode}>{record?.refCode || "-"}</Text>
            {renderBusinessName(record, "-")}
          </Cell>
          <Cell icon={<IdentificationCardIcon size={16} />} label={c("taxNumber")}>
            <Text span inherit ff="monospace">{record?.regNumber || "-"}</Text>
          </Cell>
          <Cell icon={<CalendarBlankIcon size={16} />} label={c("paymentTermLabel")}>{record?.paymentTerm?.description || "-"}</Cell>
          <Cell icon={<UserIcon size={16} />} label={c("supplierManager")}><Person staff={record?.supplierStaff} /></Cell>
          {/* The real buyer manager. The old header stamp showed the signed-in user and the current time (O79). */}
          <Cell icon={<UserIcon size={16} />} label={c("buyerManager")}><Person staff={record?.buyerStaff} /></Cell>
          <Cell icon={<UserIcon size={16} />} label={c("receiverStaff")}><Person staff={record?.receiverStaff} /></Cell>
          <Cell icon={<BuildingIcon size={16} />} label={c("receiverBranchLabel")}>{record?.receiverBranch?.name || "-"}</Cell>
          <Cell icon={<MapPinIcon size={16} />} label={c("receiverAddress")}>{record?.receiverBranch?.branchAddress || "-"}</Cell>
        </SimpleGrid>
      </Box>

      {customerBusinessId && (
        <Box>
          <CustomerOrdersSection customerBusinessId={customerBusinessId} />
        </Box>
      )}

      {/* TR-level config override: the supplier writes, the buyer reads. `networkId` is the network row, and the
          supplier side comes from `supplierBusinessId` (businessId is always the caller). */}
      {record?.networkId && (
        <Box>
          <NetworkConfigOverridePanel
            networkId={record.networkId}
            isSupplierSide={!!record.supplierBusinessId && record.supplierBusinessId === myBusinessId}
            supplierBusinessId={record.supplierBusinessId}
          />
        </Box>
      )}
    </PageLayout>
  )
}
